import { useState, useEffect, useCallback, useRef } from 'react';
import { Video } from '@meradio/shared-types';
import { waitForYouTubeAPI, isYouTubeAPIAvailable, getYouTubePlayerState } from '@/utils/youtubeAPI';

export enum YouTubePlayerError {
  INVALID_PARAM = 2,
  HTML5_ERROR = 5,
  VIDEO_NOT_FOUND = 100,
  VIDEO_NOT_EMBEDDABLE = 101,
  VIDEO_NOT_EMBEDDABLE_IN_DOMAIN = 150,
}

export interface YouTubePlayerHookReturn {
  player: YT.Player | null;
  isPlayerReady: boolean;
  currentTime: number;
  duration: number;
  playerState: YT.PlayerState;
  volume: number;
  isMuted: boolean;
  error: string | null;

  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  setVolume: (volume: number) => void;
  mute: () => void;
  unMute: () => void;
  loadVideo: (videoId: string) => void;
  destroy: () => void;

  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => YT.PlayerState;
}

interface UseYouTubePlayerOptions {
  containerId?: string;
  autoplay?: boolean;
  onReady?: (player: YT.Player) => void;
  onStateChange?: (state: YT.PlayerState) => void;
  onError?: (error: YouTubePlayerError) => void;
  onProgress?: (currentTime: number, duration: number) => void;
}

const useYouTubePlayer = (options: UseYouTubePlayerOptions = {}): YouTubePlayerHookReturn => {
  const {
    containerId = 'youtube-player',
    autoplay = false,
    onReady,
    onStateChange,
    onError,
    onProgress,
  } = options;

  const [player, setPlayer] = useState<YT.Player | null>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerState, setPlayerState] = useState<YT.PlayerState>(
    getYouTubePlayerState().UNSTARTED
  );
  const [volume, setVolumeState] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const progressInterval = useRef<NodeJS.Timeout | null>(null);
  const isInitializing = useRef(false);
  const playerRef = useRef<YT.Player | null>(null);
  const isPlayerReadyRef = useRef(false);

  const clearProgressInterval = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  }, []);

  useEffect(() => {
    playerRef.current = player;
    isPlayerReadyRef.current = isPlayerReady;
  }, [player, isPlayerReady]);

  const startProgressTracking = useCallback(() => {
    console.log('🎵 Starting progress tracking interval');
    clearProgressInterval();

    progressInterval.current = setInterval(() => {
      const currentPlayer = playerRef.current;
      const currentReady = isPlayerReadyRef.current;
      
      if (currentPlayer && currentReady) {
        try {
          const time = currentPlayer.getCurrentTime();
          const dur = currentPlayer.getDuration();
          
          console.log('🎵 Progress interval - time:', time, 'dur:', dur);
          setCurrentTime(time);
          setDuration(dur);
          
          onProgress?.(time, dur);
        } catch (err) {
          console.warn('Failed to get player progress:', err);
        }
      } else {
        console.log('🎵 Progress interval - skipped (player:', !!currentPlayer, 'ready:', currentReady, ')');
      }
    }, 1000);
  }, [onProgress, clearProgressInterval]);

  const handlePlayerReady = useCallback((event: YT.PlayerEvent) => {
    console.log('🎵 YouTube Player ready');
    const playerInstance = event.target;

    setPlayer(playerInstance);
    setIsPlayerReady(true);
    setError(null);

    const iframeInWrapper = document.querySelector('#youtube-player-wrapper iframe');
    const iframeInPlayer = document.querySelector('#youtube-player iframe');
    const playerElement = document.getElementById('youtube-player');
    
    console.log('🎵 Player ready - iframe check:', {
      iframeInWrapper: !!iframeInWrapper,
      iframeInPlayer: !!iframeInPlayer,
      playerElementType: playerElement?.tagName,
      playerElementIsIframe: playerElement?.tagName === 'IFRAME',
      iframeSrc: (iframeInWrapper || iframeInPlayer)?.getAttribute('src'),
      hasPlayVideo: typeof playerInstance.playVideo === 'function',
      hasPauseVideo: typeof playerInstance.pauseVideo === 'function',
      hasLoadVideoById: typeof playerInstance.loadVideoById === 'function',
    });

    try {
      const vol = playerInstance.getVolume();
      const muted = playerInstance.isMuted();
      setVolumeState(vol);
      setIsMuted(muted);
    } catch (err) {
      console.warn('Failed to get initial player state:', err);
    }

    try {
      const dur = playerInstance.getDuration();
      if (dur > 0) {
        setDuration(dur);
        onProgress?.(0, dur);
        console.log('🎵 Initial duration captured:', dur);
      }
    } catch (err) {
      console.warn('Failed to get initial duration:', err);
    }
    
    onReady?.(playerInstance);
  }, [onReady, onProgress]);

  const handlePlayerStateChange = useCallback((event: YT.OnStateChangeEvent) => {
    const state = event.data;
    const playerInstance = event.target;
    setPlayerState(state);

    const playerStates = getYouTubePlayerState();
    console.log('🎵 Player state changed:', state);

    if (state === playerStates.CUED || state === playerStates.BUFFERING) {
      try {
        const dur = playerInstance.getDuration();
        if (dur > 0) {
          setDuration(dur);
          const time = playerInstance.getCurrentTime();
          setCurrentTime(time);
          onProgress?.(time, dur);
          console.log('🎵 Duration captured on state change:', dur);
        }
      } catch (err) {
        console.warn('Failed to capture duration on state change:', err);
      }
    }

    if (state === playerStates.PLAYING) {
      startProgressTracking();
    } else {
      clearProgressInterval();
    }
    
    onStateChange?.(state);
  }, [onStateChange, startProgressTracking, clearProgressInterval, onProgress]);

  const handlePlayerError = useCallback((event: YT.OnErrorEvent) => {
    const errorCode = event.data as YouTubePlayerError;
    let errorMessage = 'Unknown player error';
    
    switch (errorCode) {
      case YouTubePlayerError.INVALID_PARAM:
        errorMessage = 'Invalid video parameter';
        break;
      case YouTubePlayerError.HTML5_ERROR:
        errorMessage = 'HTML5 player error';
        break;
      case YouTubePlayerError.VIDEO_NOT_FOUND:
        errorMessage = 'Video not found';
        break;
      case YouTubePlayerError.VIDEO_NOT_EMBEDDABLE:
        errorMessage = 'Video cannot be embedded';
        break;
      case YouTubePlayerError.VIDEO_NOT_EMBEDDABLE_IN_DOMAIN:
        errorMessage = 'Video cannot be embedded in this domain';
        break;
    }
    
    console.error('🎵 YouTube Player error:', errorMessage, errorCode);
    setError(errorMessage);
    clearProgressInterval();
    
    onError?.(errorCode);
  }, [onError, clearProgressInterval]);

  const initializePlayer = useCallback(async () => {
    if (isInitializing.current || player) {
      console.log('🎵 Player already initializing or initialized, skipping...');
      return;
    }

    const isAPIReady = await waitForYouTubeAPI();
    if (!isAPIReady || !isYouTubeAPIAvailable()) {
      console.error('❌ YouTube API failed to load');
      return;
    }

    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`🎵 Player container #${containerId} not found`);
      return;
    }
    
    if (!document.body.contains(container)) {
      console.warn(`🎵 Player container #${containerId} not attached to DOM`);
      return;
    }
    
    console.log('🎵 Container verified:', {
      exists: !!container,
      inDOM: document.body.contains(container),
      dimensions: { width: container.offsetWidth, height: container.offsetHeight },
      containerElement: container
    });
    
    isInitializing.current = true;

    try {
      console.log('🎵 Initializing YouTube Player with containerId...');

      const playerInstance = new window.YT.Player(containerId, {
        height: '180',
        width: '320',
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          controls: 1,
          disablekb: 0,
          enablejsapi: 1,
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          origin: window.location.origin,
        },
        events: {
          onReady: handlePlayerReady,
          onStateChange: handlePlayerStateChange,
          onError: handlePlayerError,
        },
      });

      console.log('🎵 Player instance created');

      setPlayer(playerInstance);
    } catch (err) {
      console.error('🎵 Failed to initialize YouTube Player:', err);
      setError('Failed to initialize player');
      isInitializing.current = false;
    }
  }, [
    containerId, 
    autoplay,
    player,
    handlePlayerReady,
    handlePlayerStateChange,
    handlePlayerError,
  ]);

  useEffect(() => {
    return () => {
      clearProgressInterval();
      if (player) {
        try {
          player.destroy();
        } catch (err) {
          console.warn('Failed to destroy YouTube player:', err);
        }
      }
    };
  }, [player, clearProgressInterval]);

  const play = useCallback(() => {
    if (!player || !isPlayerReady) {
      console.warn('🎵 Cannot play: player not ready');
      return;
    }
    
    try {
      console.log('🎵 Calling playVideo()');
      player.playVideo();
    } catch (err) {
      console.error('🎵 Failed to play video:', err, player);
      setError('Failed to play video');
    }
  }, [player, isPlayerReady]);

  const pause = useCallback(() => {
    if (!player || !isPlayerReady) {
      console.warn('🎵 Cannot pause: player not ready');
      return;
    }
    
    try {
      console.log('🎵 Calling pauseVideo()');
      player.pauseVideo();
    } catch (err) {
      console.error('🎵 Failed to pause video:', err, player);
      setError('Failed to pause video');
    }
  }, [player, isPlayerReady]);

  const seekTo = useCallback((time: number) => {
    if (player && isPlayerReady) {
      try {
        player.seekTo(time, true);
      } catch (err) {
        console.error('Failed to seek video:', err);
        setError('Failed to seek video');
      }
    }
  }, [player, isPlayerReady]);

  const setVolume = useCallback((vol: number) => {
    if (!player || !isPlayerReady) {
      console.warn('🎵 Cannot set volume: player not ready');
      return;
    }
    
    try {
      const clampedVolume = Math.max(0, Math.min(100, vol));
      console.log('🎵 Setting volume to:', clampedVolume);
      player.setVolume(clampedVolume);
      setVolumeState(clampedVolume);
    } catch (err) {
      console.error('🎵 Failed to set volume:', err, player);
    }
  }, [player, isPlayerReady]);

  const mute = useCallback(() => {
    if (!player || !isPlayerReady) {
      console.warn('🎵 Cannot mute: player not ready');
      return;
    }
    
    try {
      player.mute();
      setIsMuted(true);
    } catch (err) {
      console.error('🎵 Failed to mute video:', err);
    }
  }, [player, isPlayerReady]);

  const unMute = useCallback(() => {
    if (!player || !isPlayerReady) {
      console.warn('🎵 Cannot unmute: player not ready');
      return;
    }
    
    try {
      player.unMute();
      setIsMuted(false);
    } catch (err) {
      console.error('🎵 Failed to unmute video:', err);
    }
  }, [player, isPlayerReady]);

  const loadVideo = useCallback((videoId: string) => {
    if (!player) {
      console.log('🎵 Player doesn\'t exist, initializing with video:', videoId);

      if (!window.YT || !window.YT.Player) {
        console.error('🎵 YouTube API not ready');
        setError('YouTube API not ready');
        return;
      }

      const container = document.getElementById(containerId);
      if (!container) {
        console.error('🎵 Player container not found:', containerId);
        setError('Player container not found');
        return;
      }
      
      if (isInitializing.current) {
        console.log('🎵 Already initializing...');
        return;
      }
      
      isInitializing.current = true;
      setError(null);

      try {
        const playerInstance = new window.YT.Player(containerId, {
          height: '180',
          width: '320',
          videoId: videoId,
          playerVars: {
            autoplay: autoplay ? 1 : 0,
            controls: 1,
            disablekb: 0,
            enablejsapi: 1,
            fs: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: handlePlayerReady,
            onStateChange: handlePlayerStateChange,
            onError: handlePlayerError,
          },
        });

        console.log('🎵 Player instance created with initial video');
        setPlayer(playerInstance);
      } catch (err) {
        console.error('🎵 Failed to create player:', err);
        setError('Failed to create player');
        isInitializing.current = false;
      }
      
      return;
    }

    if (!isPlayerReady) {
      console.warn('🎵 Player not ready yet, cannot load video');
      return;
    }
    
    try {
      console.log('🎵 Loading SUBSEQUENT video:', videoId);
      console.log('🎵 Player instance:', player);
      console.log('🎵 Has loadVideoById method:', typeof player.loadVideoById);
      setError(null);

      setDuration(0);
      setCurrentTime(0);
      
      player.loadVideoById(videoId);
      console.log('🎵 loadVideoById called successfully');
    } catch (err) {
      console.error('🎵 Failed to load video:', err, player);
      setError('Failed to load video');
    }
  }, [player, isPlayerReady, containerId, autoplay, handlePlayerReady, handlePlayerStateChange, handlePlayerError]);

  const destroy = useCallback(() => {
    clearProgressInterval();
    isInitializing.current = false;
    setIsPlayerReady(false);
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    setPlayer(null);
  }, [clearProgressInterval]);

  const getCurrentTime = useCallback(() => {
    if (player && isPlayerReady) {
      try {
        return player.getCurrentTime();
      } catch (err) {
        console.error('Failed to get current time:', err);
      }
    }
    return 0;
  }, [player, isPlayerReady]);

  const getDuration = useCallback(() => {
    if (player && isPlayerReady) {
      try {
        return player.getDuration();
      } catch (err) {
        console.error('Failed to get duration:', err);
      }
    }
    return 0;
  }, [player, isPlayerReady]);

  const getPlayerState = useCallback(() => {
    if (player && isPlayerReady) {
      try {
        return player.getPlayerState();
      } catch (err) {
        console.error('Failed to get player state:', err);
      }
    }
    return getYouTubePlayerState().UNSTARTED;
  }, [player, isPlayerReady]);

  return {
    player,
    isPlayerReady,
    currentTime,
    duration,
    playerState,
    volume,
    isMuted,
    error,

    play,
    pause,
    seekTo,
    setVolume,
    mute,
    unMute,
    loadVideo,
    destroy,

    getCurrentTime,
    getDuration,
    getPlayerState,
  };
};

export default useYouTubePlayer;
